import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Loader from '../common/Loader.js';
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import attendanceService from '../../services/AttendanceService.js';
import { LogIn, LogOut, Users, ArrowLeft } from 'lucide-react';
import Button from '../common/Button.js';
import { useTranslation } from 'react-i18next';
import { getCurrentDevicePosition } from '../../utils/deviceLocation.js';


const BulkAttendanceLogin = () => {
  const { user: currentUser, syncSessionFromServer } = useAuth();
  const navigate = useNavigate();

  const [candidates, setCandidates] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitAction, setSubmitAction] = useState(null);
  const [companyLabel, setCompanyLabel] = useState(currentUser?.company_name || 'Your company');
  const [sessionUser, setSessionUser] = useState(currentUser);

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      setLoading(true);

      const storedRaw = sessionStorage.getItem('user');
      let activeUser = currentUser;
      if (storedRaw) {
        const storedUser = JSON.parse(storedRaw);
        if (storedUser?.token && syncSessionFromServer) {
          const synced = await syncSessionFromServer(storedUser);
          if (synced) {
            activeUser = synced;
            setSessionUser(synced);
          }
        }
      }

      const response = await attendanceService.getBulkLoginCandidates();
      const operatorCompanyId = response.operator_company_id;
      const operatorCompanyName = response.operator_company_name;

      if (
        activeUser?.company_id &&
        operatorCompanyId &&
        activeUser.company_id !== operatorCompanyId
      ) {
        Alert.error(
          `Company mismatch: you are logged in as ${activeUser.company_name || activeUser.company_id}, but the server returned ${operatorCompanyName}. Please log out and log in again.`
        );
        setCandidates([]);
        return;
      }

      const scoped = (response.data || []).filter(
        (user) =>
          !operatorCompanyId ||
          !user.company_id ||
          user.company_id === operatorCompanyId
      );

      setCandidates(scoped);
      setCompanyLabel(operatorCompanyName || activeUser?.company_name || 'Your company');
      setSelectedUserIds([]);
    } catch (err) {
      const status = err.response?.status;
      const message =
        err.response?.data?.error ||
        (status === 404
          ? 'Bulk login API not found. Restart the backend server and try again.'
          : 'Failed to load users');
      Alert.error(message);
    } finally {
      setLoading(false);
    }
  };

  const userCanLogin = (user) => user?.today_status === 'none';
  const userCanLogout = (user) => user?.today_status === 'active';
  const getCandidate = (userId) => candidates.find((user) => user.user_id === userId);

  const loginSelectableCandidates = candidates.filter(userCanLogin);
  const logoutSelectableCandidates = candidates.filter(userCanLogout);
  const actionSelectableCandidates = candidates.filter(
    (user) => userCanLogin(user) || userCanLogout(user)
  );

  const selectedLoginCount = selectedUserIds.filter((id) => userCanLogin(getCandidate(id))).length;
  const selectedLogoutCount = selectedUserIds.filter((id) => userCanLogout(getCandidate(id))).length;
  const allActionSelected =
    actionSelectableCandidates.length > 0 &&
    actionSelectableCandidates.every((user) => selectedUserIds.includes(user.user_id));

  const handleSelectAll = () => {
    setSelectedUserIds(actionSelectableCandidates.map((user) => user.user_id));
  };

  const handleClearSelection = () => {
    setSelectedUserIds([]);
  };

  const toggleSelectAll = () => {
    if (allActionSelected) {
      handleClearSelection();
    } else {
      handleSelectAll();
    }
  };

  const isUserSelectable = (user) => userCanLogin(user) || userCanLogout(user);

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const getCurrentPosition = () => getCurrentDevicePosition();

  const getTodayStatusLabel = (user) => {
    if (user.today_status === 'completed') return 'Completed today';
    if (user.today_status === 'active') return 'Logged in today';
    return 'Not logged in';
  };

  const handleBulkLogin = async () => {
    if (selectedLoginCount === 0) {
      Alert.error('Select at least one user who can be logged in');
      return;
    }

    const loginUserIds = selectedUserIds.filter((id) => userCanLogin(getCandidate(id)));

    const confirmed = await Alert.confirm(
      `Mark attendance login for ${loginUserIds.length} user(s) at the same time? Each user can only login once per day.`,
      'Bulk Attendance Login'
    );
    if (!confirmed) return;

    try {
      setSubmitting(true);
      setSubmitAction('login');
      Alert.info('Getting your location...');
      const position = await getCurrentPosition();
      const response = await attendanceService.bulkLoginAttendance(
        loginUserIds,
        position.latitude,
        position.longitude
      );

      const summary = response.summary || {};
      if (summary.logged_in > 0) {
        Alert.success(
          `Logged in ${summary.logged_in} user(s) at ${new Date(response.login_time).toLocaleTimeString('en-IN')}`
        );
        await fetchCandidates();
      } else {
        Alert.error('No users were logged in. Selected users may have already logged in or completed attendance today.');
      }
    } catch (err) {
      Alert.error(err.response?.data?.error || err.message || 'Bulk login failed');
    } finally {
      setSubmitting(false);
      setSubmitAction(null);
    }
  };

  const handleBulkLogout = async () => {
    if (selectedLogoutCount === 0) {
      Alert.error('Select at least one user with an active session');
      return;
    }

    const logoutUserIds = selectedUserIds.filter((id) => userCanLogout(getCandidate(id)));

    const confirmed = await Alert.confirm(
      `Mark attendance logout for ${logoutUserIds.length} user(s) at the same time?`,
      'Bulk Attendance Logout'
    );
    if (!confirmed) return;

    try {
      setSubmitting(true);
      setSubmitAction('logout');
      Alert.info('Getting your location...');
      const position = await getCurrentPosition();
      const response = await attendanceService.bulkLogoutAttendance(
        logoutUserIds,
        position.latitude,
        position.longitude
      );

      const summary = response.summary || {};
      if (summary.logged_out > 0) {
        Alert.success(
          `Logged out ${summary.logged_out} user(s) at ${new Date(response.logout_time).toLocaleTimeString('en-IN')}`
        );
        await fetchCandidates();
      } else {
        Alert.error('No users were logged out. Selected users may not have an active session.');
      }
    } catch (err) {
      const status = err.response?.status;
      const message =
        err.response?.data?.error ||
        (status === 404
          ? 'Bulk logout API not found. Restart the backend server and try again.'
          : err.message || 'Bulk logout failed');
      Alert.error(message);
    } finally {
      setSubmitting(false);
      setSubmitAction(null);
    }
  };

  return (
    <DashboardLayout>
      <div>
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Button variant="ghost" onClick={() => navigate('/attendance')} className="px-2 py-1">
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <Users className="w-6 h-6 text-primary" />
              <h1 className="text-xl sm:text-2xl font-bold text-primary">Bulk Attendance Login/Logout</h1>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={fetchCandidates} disabled={loading || submitting}>
              Refresh
            </Button>
            <Button variant="secondary" onClick={handleSelectAll} disabled={loading || actionSelectableCandidates.length === 0}>
              Select All
            </Button>
            <Button variant="secondary" onClick={handleClearSelection} disabled={loading || selectedUserIds.length === 0}>
              Clear Selection
            </Button>
            <Button
              variant="secondary"
              icon={LogOut}
              onClick={handleBulkLogout}
              loading={submitting && submitAction === 'logout'}
              disabled={loading || submitting || selectedLogoutCount === 0}
            >
              Bulk Logout ({selectedLogoutCount})
            </Button>
            <Button
              variant="primary"
              icon={LogIn}
              onClick={handleBulkLogin}
              loading={submitting && submitAction === 'login'}
              disabled={loading || submitting || selectedLoginCount === 0}
            >
              Bulk Login ({selectedLoginCount})
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          {loading ? (
            <Loader fullScreen={false} size="default" message="Loading company users..." />
          ) : candidates.length === 0 ? (
            <div className="p-8 text-center text-gray-600">No users found for bulk login</div>
          ) : (
            <>
              <div className="hidden md:block overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={allActionSelected}
                          onChange={toggleSelectAll}
                          disabled={actionSelectableCandidates.length === 0}
                          className="rounded border-gray-300 text-orange-500 focus:ring-blue-400"
                          title="Select all"
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Online</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {candidates.map((user) => (
                      <tr key={user.user_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedUserIds.includes(user.user_id)}
                            onChange={() => toggleUserSelection(user.user_id)}
                            disabled={!isUserSelectable(user)}
                            className="rounded border-gray-300 text-orange-500 focus:ring-blue-400 disabled:opacity-40"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{user.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{user.contact || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{user.email || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{user.role_name || 'N/A'}</td>
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            user.today_status === 'none'
                              ? 'bg-gray-100 text-gray-700'
                              : user.today_status === 'active'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-amber-100 text-amber-700'
                          }`}>
                            {getTodayStatusLabel(user)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-2.5 h-2.5 rounded-full ${user.is_online ? 'bg-green-500' : 'bg-red-500'}`} />
                            <span className="text-sm text-gray-600">{user.is_online ? 'Online' : 'Offline'}</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden p-4 space-y-3">
                <div className="flex gap-2 pb-2 border-b">
                  <Button variant="secondary" size="sm" onClick={handleSelectAll} disabled={actionSelectableCandidates.length === 0}>
                    Select All
                  </Button>
                  <Button variant="secondary" size="sm" onClick={handleClearSelection} disabled={selectedUserIds.length === 0}>
                    Clear
                  </Button>
                </div>
                {candidates.map((user) => (
                  <div key={user.user_id} className="border rounded-lg p-4 bg-gray-50">
                    <label className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user.user_id)}
                        onChange={() => toggleUserSelection(user.user_id)}
                        disabled={!isUserSelectable(user)}
                        className="mt-1 rounded border-gray-300 text-orange-500 focus:ring-blue-400 disabled:opacity-40"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-600">{user.contact || 'N/A'}</p>
                        <p className="text-sm text-gray-600 break-all">{user.email || 'N/A'}</p>
                        <p className="text-xs text-gray-500 mt-1">{user.role_name}</p>
                        <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-medium ${
                          user.today_status === 'none'
                            ? 'bg-gray-200 text-gray-700'
                            : user.today_status === 'active'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-amber-100 text-amber-700'
                        }`}>
                          {getTodayStatusLabel(user)}
                        </span>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BulkAttendanceLogin;
